/**
 * nav-tracker.js — Best-effort tracking de navegación (v7.8.0 Nav Sprint 1).
 *
 * Batch de eventos cada 5s o al unload. Fire-and-forget: nunca bloquea UI ni
 * loggea errores al usuario. Backend descarta silencioso si no hay auth.
 *
 * Estrategia:
 *   - Queue local, flush cada 5s O al llegar a MAX_QUEUE.
 *   - En pagehide / visibilitychange:hidden → flush inmediato con keepalive.
 *   - `fetch({keepalive:true})` sobrevive unload en navegadores modernos.
 *     No usamos sendBeacon porque el auth exige header Authorization.
 *
 * Uso:
 *   import { trackNav } from "@/lib/nav-tracker";
 *   trackNav("/tareas", "sidebar");
 */
import { auth } from "./firebase.js";

const ENDPOINT = `${import.meta.env.VITE_API_URL || ""}/api/nav/track`;
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 50;

let queue = [];
let flushTimer = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

async function flush() {
  if (queue.length === 0) return;
  const events = queue.splice(0, MAX_QUEUE);
  const body = JSON.stringify({ events });

  try {
    const user = auth.currentUser;
    if (!user) return;  // silent drop si no hay auth

    const idToken = await user.getIdToken(false).catch(() => null);
    if (!idToken) return;

    fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${idToken}`,
      },
      body,
      keepalive: true,  // sobrevive page unload en Chrome/Edge/Safari 15+
    }).catch(() => {});
  } catch {
    // Silent — nunca romper por tracking.
  }
}

export function trackNav(path, source = "sidebar") {
  if (!path || typeof path !== "string") return;
  queue.push({ path, source });
  if (queue.length >= MAX_QUEUE) {
    flush();
  } else {
    scheduleFlush();
  }
}

// Flush on unload / hide.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => flush(), { capture: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}
